<script lang="ts" setup>
import { onMounted, computed, watch, type VNode } from "vue";
import type { VIllustrationProps, Illustration } from "./VIllustration.types";
import illustrationsDimensions from "../assets/illustrations-dimensions.json";
import useLoadIllustration from "@magnit/illustrations/src/composables/useLoadIllustration";

const props = withDefaults(defineProps<VIllustrationProps>(), {
  project: "base",
});
const slots = defineSlots<{
  default(): VNode;
}>();

const asyncIllustration = computed(() => Boolean(!slots.default && props.name));
const illustrationsStyle = computed(() => {
  try {
    if (props.customSize || !asyncIllustration.value) return null;
    const dimensionsMap = (illustrationsDimensions.dimensions[props.project] ??
      {}) as Record<Illustration, { width: number; height: number }>;
    if (!dimensionsMap[props.name]) return null;
    const { width, height } = dimensionsMap[props.name];
    return { width: `${width}px`, height: `${height}px` };
  } catch {
    return null;
  }
});

const { fetchAsyncIllustration, loadedIllustration } = useLoadIllustration(
  props.name,
  props.project,
  asyncIllustration,
);

onMounted(fetchAsyncIllustration);
watch(() => props.name, fetchAsyncIllustration);
</script>

<template>
  <div class="pl-illustration" :style="illustrationsStyle">
    <slot>
      <figure
        v-if="loadedIllustration"
        v-bind="$attrs"
        v-html="loadedIllustration"
      />
    </slot>
  </div>
</template>

<style lang="postcss" scoped>
.pl-illustration {
  position: relative;

  > figure {
    height: 100%;
    margin: 0;

    > :deep(svg, img) {
      width: 100%;
      height: 100%;
    }
  }
}
</style>
